import React, { useState, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X, Loader2, Upload, FileText, CheckCircle2, AlertTriangle,
  ArrowRight, Sparkles, UserPlus, Layers, Trash2, ChevronDown, ChevronRight,
} from "lucide-react";
import { api, API, fmtDate } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import { confirmDialog } from "../utils/dialogs";

/**
 * BulkLabImportWizard — bulk-import a folder of lab-report PDFs.
 *
 * Trade-off vs the single-report wizard: this UI is deliberately compact
 * so operators can eyeball a table of 20-40 files. Per-row expand lets
 * you fine-tune individual imports; the default "Import all confirmed"
 * button processes everything the fuzzy-matcher was confident about.
 *
 * Flow:
 *   upload → analysing (parallel extraction on backend) → review grid →
 *   per-row [Import / Skip / Edit] → summary
 */
export default function BulkLabImportWizard({ open, onClose, onCommitted }) {
  const [step, setStep] = useState("upload");         // "upload" | "review" | "done"
  const [results, setResults] = useState([]);          // Rows keyed by index
  const [existingSailors, setExistingSailors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState({ done: 0, total: 0, phase: "", current: "", stage_label: "" });
  const [summary, setSummary] = useState(null);       // {imported, skipped, errored}
  const [expanded, setExpanded] = useState(new Set());
  // If truthy, show a confirm dialog before actually closing. Set by
  // `guardedClose` when there's unsaved extracted-but-not-committed
  // work in progress. Prevents an accidental backdrop click from
  // discarding 60+ MB of PDF uploads.
  const [confirmClose, setConfirmClose] = useState(false);
  const fileInputRef = useRef(null);

  // Anything in-flight that would be lost on close?
  //   - `busy` covers the upload + extraction streaming phase
  //   - "review" with pending/committing rows covers the review phase
  //   after extraction completes but before Finish is clicked.
  // Deliberately skips "done" (nothing to lose) and the empty upload
  // step (no data yet).
  const hasUnsavedWork = () => {
    if (busy) return true;
    if (step === "review" && results.some((r) =>
      r._commit_status === "pending" || r._commit_status === "committing"
    )) return true;
    return false;
  };

  const guardedClose = () => {
    if (hasUnsavedWork()) {
      setConfirmClose(true);
    } else {
      onClose?.();
    }
  };

  useEscClose(open, guardedClose);

  // ---------------------------------------------------------------------
  // Folder-drop support.
  //
  // When a user drags a *folder* onto the drop-zone, `dataTransfer.files`
  // is empty on Chrome/Edge/Firefox — the folder itself doesn't count as
  // a File. The only way to enumerate a dropped folder is to iterate
  // `dataTransfer.items[i].webkitGetAsEntry()` and recurse. Without this,
  // the UI showed "No PDF files found" even when the folder was full of
  // PDFs, because we were counting an empty FileList.
  //
  // Safari doesn't support webkitGetAsEntry — in that case the fallback
  // to `dataTransfer.files` still handles multi-file drops correctly.
  // ---------------------------------------------------------------------
  const walkEntry = (entry) => new Promise((resolve) => {
    if (!entry) { resolve([]); return; }
    if (entry.isFile) {
      entry.file(
        (file) => resolve([file]),
        () => resolve([]),   // permission errors → skip silently
      );
      return;
    }
    if (entry.isDirectory) {
      const reader = entry.createReader();
      const collected = [];
      // readEntries only returns a *page* at a time — you have to keep
      // calling until it returns an empty array. Missing this is the
      // classic reason folder-drop "works for small folders but not big
      // ones."
      const readAll = () => {
        reader.readEntries(async (entries) => {
          if (!entries.length) { resolve(collected); return; }
          const nested = await Promise.all(entries.map(walkEntry));
          nested.forEach((arr) => collected.push(...arr));
          readAll();
        }, () => resolve(collected));
      };
      readAll();
      return;
    }
    resolve([]);
  });

  const extractFilesFromDrop = async (dt) => {
    // Prefer the webkitGetAsEntry path so folders can be walked; fall
    // back to dataTransfer.files for browsers without support (Safari).
    const items = dt?.items;
    if (items && items.length && typeof items[0].webkitGetAsEntry === "function") {
      const entries = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) entries.push(entry);
      }
      if (entries.length) {
        const nested = await Promise.all(entries.map(walkEntry));
        return nested.flat();
      }
    }
    return Array.from(dt?.files || []);
  };

  useEffect(() => {
    if (open && step === "upload") {
      api.get("/sailors")
        .then((r) => setExistingSailors(r.data || []))
        .catch(() => setExistingSailors([]));
    }
  }, [open, step]);

  // Kept indexed by filename so a "Retry failed" click can find the
  // originally-picked File object without asking the coach to re-pick.
  // Refs (not state) because we don't need re-renders — the values only
  // matter when the coach clicks retry.
  const filesRef = useRef(new Map());

  useEffect(() => {
    if (!open) {
      setStep("upload"); setResults([]); setError(""); setSummary(null);
      setExpanded(new Set()); setProgress({ done: 0, total: 0, phase: "", current: "", stage_label: "" });
      setConfirmClose(false);
      filesRef.current = new Map();
    }
  }, [open]);

  const onFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const all = Array.from(fileList);
    const pdfs = all.filter((f) => (f.name || "").toLowerCase().endsWith(".pdf"));
    if (pdfs.length === 0) {
      // If nothing came through at all, the most likely culprit is a
      // dropped folder that the browser couldn't enumerate (Safari
      // doesn't support directory drops). If files came through but
      // none had .pdf extensions, tell the operator which files we
      // did see — helps debug corporate-locked machines where PDFs
      // arrive with mangled names.
      if (all.length === 0) {
        setError(
          "The browser returned no files. If you dragged a folder, try clicking \"Pick a folder\" instead — some browsers (Safari especially) can't enumerate dropped folders."
        );
      } else {
        const sample = all.slice(0, 3).map((f) => f.name || "(unnamed)").join(", ");
        setError(
          `No .pdf files found — the browser handed back ${all.length} file${all.length === 1 ? "" : "s"} (first few: ${sample}). Only files ending in .pdf can be bulk-imported.`
        );
      }
      return;
    }
    if (pdfs.length > 60) { setError(`Please select at most 60 PDFs at a time. Got ${pdfs.length}.`); return; }
    // Snapshot every File keyed by name so a later "Retry failed" click
    // can re-upload just the errored subset without asking the coach to
    // re-pick the folder. Overwrites any earlier picks — a fresh drop
    // starts a fresh batch.
    filesRef.current = new Map(pdfs.map((f) => [f.name, f]));
    await _extractPdfs(pdfs);
  };

  /**
   * Retry just the files that failed extraction on the first pass. Common
   * cause: transient Anthropic stream hiccup on a long Claude call for
   * an unusual PDF layout. A single retry usually clears it — Claude
   * doesn't cache the previous attempt, so we get a fresh streaming call
   * with the full 3× backoff budget again.
   */
  const retryFailed = async () => {
    const failed = (results || []).filter((r) => r.status === "error");
    if (failed.length === 0) return;
    const pdfs = failed
      .map((r) => filesRef.current.get(r.filename))
      .filter(Boolean);
    if (pdfs.length === 0) {
      setError("Couldn't recover the original files for retry — please pick the folder again.");
      return;
    }
    // Remove the errored rows from the review grid so the retry-result
    // rows land in their place (indices are re-assigned by `_extractPdfs`).
    const keepIdx = new Set(
      (results || [])
        .filter((r) => r.status !== "error")
        .map((r) => r._idx),
    );
    setResults((prev) => prev.filter((r) => keepIdx.has(r._idx)));
    await _extractPdfs(pdfs, { append: true });
  };

  /**
   * Shared extraction pipeline — used by both the initial upload flow
   * and the "Retry failed" button. `append=true` merges new results into
   * the existing review grid instead of replacing it, so a coach who
   * retries 2 files out of a 15-file batch doesn't lose the 13 successes.
   */
  const _extractPdfs = async (pdfs, { append = false } = {}) => {
    if (pdfs.length > 60) { setError(`Please select at most 60 PDFs at a time. Got ${pdfs.length}.`); return; }
    setBusy(true); setError("");
    setProgress({
      done: 0, total: pdfs.length,
      phase: "uploading", current: "",
      stage_label: `Preparing ${pdfs.length} file${pdfs.length === 1 ? "" : "s"}…`,
    });
    try {
      const form = new FormData();
      pdfs.forEach((f) => form.append("files", f));
      // Use XMLHttpRequest so we can track BOTH upload progress
      // (large multipart bodies of 20+ PDFs can take 30-60 s to reach
      // the server on residential broadband) AND progressive response
      // parsing (NDJSON events emitted by the server). fetch() doesn't
      // support upload progress today.
      const finalResults = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/labs/scan-import/bulk-preview-stream`);
        // Send the httpOnly auth cookie with this cross-instance XHR —
        // withCredentials MUST be set BEFORE any send(). Same-origin
        // here so no CORS pre-flight cost.
        xhr.withCredentials = true;
        // Track upload progress — this fires while bytes travel from
        // the browser to the server, BEFORE the server starts sending
        // its response. Coaches see a real % while their 40 MB of PDFs
        // are being pushed to the backend.
        xhr.upload.onprogress = (ev) => {
          if (!ev.lengthComputable) return;
          const pct = Math.round((ev.loaded / ev.total) * 100);
          setProgress((p) => ({
            ...p,
            phase: "uploading",
            done: 0,
            stage_label: `Uploading ${Math.round(ev.loaded / (1024 * 1024))}/${Math.round(ev.total / (1024 * 1024))} MB · ${pct}%`,
          }));
        };
        // Server-side warm-up counter. `xhr.upload.onload` fires the
        // moment the browser has sent the last byte — but the ingress
        // still has to buffer, FastAPI has to parse the multipart body,
        // and every UploadFile has to be read into memory before the
        // first `start` NDJSON event can be emitted. On a 20+ file
        // batch this is a real 2-6 s pause. Show elapsed seconds so
        // coaches see the UI is alive and not locked up.
        let serverWaitTimer = null;
        const clearServerWaitTimer = () => {
          if (serverWaitTimer) {
            clearInterval(serverWaitTimer);
            serverWaitTimer = null;
          }
        };
        xhr.upload.onload = () => {
          const t0 = Date.now();
          setProgress((p) => ({
            ...p, phase: "uploading",
            stage_label: `Server receiving your PDFs…`,
          }));
          clearServerWaitTimer();
          serverWaitTimer = setInterval(() => {
            const sec = Math.round((Date.now() - t0) / 1000);
            const msg = sec < 4
              ? `Server receiving your PDFs… (${sec}s)`
              : `Server unpacking ${pdfs.length} PDF${pdfs.length === 1 ? "" : "s"}… (${sec}s)`;
            setProgress((p) =>
              // Only overwrite the label while we're still in the
              // pre-`start` phase; the moment the NDJSON stream begins
              // emitting `file_start` / `file_done` we hand control
              // back to the per-file progress writer below.
              p.phase === "uploading" ? { ...p, stage_label: msg } : p);
          }, 500);
        };
        // Incrementally parse the NDJSON body as it arrives. `xhr.responseText`
        // grows as new chunks reach the browser, so we keep a cursor
        // into it and only parse new bytes.
        let cursor = 0;
        let doneCount = 0;
        let capturedResults = null;
        // Every file_done event's payload is also accumulated here so that
        // if the stream gets cut before the final `done` event arrives
        // (production CDN / ingress idle-timeout on a long Claude call —
        // e.g., Mani's non-standard hospital PDF), we can still salvage
        // what was extracted and let the coach continue with the partial
        // batch instead of throwing away 15 already-parsed reports.
        const partialResults = [];
        const processNewText = () => {
          const buf = xhr.responseText || "";
          if (buf.length <= cursor) return;
          const fresh = buf.slice(cursor);
          cursor = buf.length;
          // The last line may be partial — keep it for next tick.
          const nl_last = fresh.lastIndexOf("\n");
          if (nl_last < 0) { cursor -= fresh.length; return; }
          const lines = fresh.slice(0, nl_last).split("\n");
          cursor = cursor - (fresh.length - nl_last - 1);
          for (const rawLine of lines) {
            const line = rawLine.trim();
            if (!line) continue;
            let evt;
            try { evt = JSON.parse(line); }
            catch { continue; }
            if (evt.type === "start") {
              clearServerWaitTimer();
              setProgress((p) => ({
                ...p, total: evt.total, done: 0,
                phase: "extracting",
                stage_label: `Extracting ${evt.total} PDF${evt.total === 1 ? "" : "s"}…`,
              }));
            } else if (evt.type === "file_start") {
              setProgress((p) => ({
                ...p, phase: "extracting", current: evt.filename,
                stage_label: `Extracting ${evt.filename}…`,
              }));
            } else if (evt.type === "file_done") {
              doneCount += 1;
              if (evt.result) partialResults.push(evt.result);
              const okOrErr = evt.result?.status === "ok" ? "✓" : "✗";
              const src = evt.raw_source === "deterministic" ? "fast" :
                          (evt.raw_source === "vision" ? "OCR" :
                          (evt.raw_source === "text" ? "LLM" : ""));
              const ms = evt.elapsed_ms;
              const tag = src && ms != null ? ` (${src} · ${(ms / 1000).toFixed(1)}s)` : "";
              setProgress((p) => ({
                ...p, done: doneCount, current: evt.filename,
                phase: "extracting", in_flight: [],
                stage_label: `${okOrErr} ${evt.filename}${tag}  ·  ${doneCount}/${p.total} done`,
              }));
            } else if (evt.type === "heartbeat") {
              const live = evt.in_flight || [];
              const label = live.length === 0
                ? `${evt.done}/${evt.total} done — waiting…`
                : live.length === 1
                  ? `Extracting ${live[0].filename} · ${live[0].elapsed_s.toFixed(0)}s so far`
                  : `Extracting ${live.length} PDFs (longest: ${Math.max(...live.map(x => x.elapsed_s)).toFixed(0)}s)`;
              setProgress((p) => ({
                ...p, done: evt.done, phase: "extracting",
                in_flight: live, stage_label: label,
                current: live[0]?.filename || p.current,
              }));
            } else if (evt.type === "done") {
              capturedResults = evt.results || [];
            }
          }
        };
        xhr.onreadystatechange = () => {
          // readyState 3 = HEADERS_RECEIVED + LOADING (chunks arriving)
          if (xhr.readyState === 3) processNewText();
        };
        xhr.onload = () => {
          clearServerWaitTimer();
          processNewText();   // flush any tail
          if (xhr.status >= 200 && xhr.status < 300) {
            // Prefer the authoritative `done` payload when we got one.
            // Otherwise fall back to whatever file_done events we captured
            // before the stream was cut — better to show partial rows the
            // coach can act on than throw away 15 minutes of extraction
            // because a CDN timeout ate the final event.
            resolve(capturedResults != null ? capturedResults : partialResults);
          } else {
            let msg = "";
            try { msg = JSON.parse(xhr.responseText)?.detail || ""; }
            catch { msg = xhr.responseText || ""; }
            reject(new Error(msg || `Bulk extract failed (HTTP ${xhr.status})`));
          }
        };
        xhr.onerror = () => {
          clearServerWaitTimer();
          // Network failure mid-stream — same salvage logic. If we got any
          // completed files, hand them back so the coach can commit them
          // and just re-upload the missing ones instead of restarting from
          // zero. This is the difference between "wasted 20 min" and "OK,
          // 12 of 15 came through, let me redo the last 3".
          if (partialResults.length > 0) {
            resolve(partialResults);
          } else {
            reject(new Error("Network error during bulk import."));
          }
        };
        xhr.ontimeout = () => {
          clearServerWaitTimer();
          if (partialResults.length > 0) {
            resolve(partialResults);
          } else {
            reject(new Error("Bulk import timed out."));
          }
        };
        xhr.send(form);
      });
      if (!finalResults || finalResults.length === 0) {
        throw new Error("Stream ended without a final result. Try uploading fewer PDFs at once, or split the batch.");
      }
      // Attach per-row UI state (chosen sailor, chosen date, chosen markers).
      // When we're APPENDING (retry-failed path), the new rows must get
      // fresh `_idx` values that don't collide with the still-visible
      // successful rows — otherwise `setRow(idx, …)` would patch the
      // wrong row later.
      const idxBase = append
        ? (results.reduce((m, r) => Math.max(m, r._idx), -1) + 1)
        : 0;
      const rows = finalResults.map((res, i) => {
        const detectedName = res.extracted?.patient_name || "";
        const bestScore = res.candidates?.[0]?.score || 0;
        return {
          ...res,
          _idx: idxBase + i,
          _sailor_id: res.suggested_sailor_id || "",
          _test_date: res.extracted?.test_date || "",
          _detected_name: detectedName,
          _best_score: bestScore,
          _needs_review: !res.suggested_sailor_id
                        || (res.candidates?.[0]?.has_lab_on_date)
                        || bestScore < 0.75
                        || Boolean(res.ambiguous_match),
          _ambiguous: Boolean(res.ambiguous_match),
          _commit_status: "pending",       // "pending" | "imported" | "skipped" | "error"
          _commit_error: null,
          _markers_selected: (res.markers || []).map(
            (m) => Boolean(m.canonical_key || m.is_new_marker) && m.value_is_numeric,
          ),
        };
      });
      if (append) {
        setResults((prev) => [...prev, ...rows]);
      } else {
        setResults(rows);
      }
      // Auto-expand any row that needs coach attention so it's obvious.
      // Start every row collapsed — coaches asked for a compact grid
      // by default. The `_needs_review` flag still surfaces via the
      // Status column ("Needs review" badge) and the row's chevron
      // remains one click away.
      setExpanded(new Set());
      setProgress((p) => ({
        ...p, phase: "done",
        stage_label: `Done — ${rows.length}/${p.total} ${append ? "retried" : "ready to review"}`,
      }));
      setStep("review");
    } catch (e) {
      setError(e?.message || "Bulk extraction failed");
    } finally {
      setBusy(false);
    }
  };

  const setRow = (idx, patch) => {
    setResults((prev) => prev.map((r) => r._idx === idx ? { ...r, ...patch } : r));
  };

  const toggleExpand = (idx) => {
    setExpanded((prev) => {
      const s = new Set(prev);
      if (s.has(idx)) s.delete(idx); else s.add(idx);
      return s;
    });
  };

  const stats = useMemo(() => {
    // Same simple mismatch heuristic used by BulkRow — kept here so the
    // "Import all" confirmation gate can preview which rows have
    // warnings before the operator commits them.
    const nameTokens = (s) => new Set(
      (s || "").toLowerCase().split(/\s+/).map((t) => t.replace(/[^a-z]/g, "")).filter((t) => t.length >= 3),
    );
    const normG = (g) => {
      const c = (g || "").trim().toLowerCase().charAt(0);
      return c === "m" ? "m" : c === "f" ? "f" : "";
    };
    const hasMismatch = (r) => {
      const sel = existingSailors.find((s) => s.id === r._sailor_id);
      if (!sel) return false;
      const dName = (r._detected_name || "").toLowerCase();
      const dGender = r.extracted?.patient_gender || "";
      if (dName) {
        const a = nameTokens(dName);
        const b = nameTokens(sel.name || "");
        let overlap = false;
        for (const t of a) if (b.has(t)) { overlap = true; break; }
        if (!overlap) return true;
      }
      if (normG(dGender) && normG(sel.gender) && normG(dGender) !== normG(sel.gender)) return true;
      const candIds = new Set((r.candidates || []).map((c) => c.sailor_id));
      if (candIds.size > 0 && !candIds.has(sel.id)) return true;
      return false;
    };
    const ok = results.filter((r) => r.status === "ok");
    const ready = ok.filter((r) => r._sailor_id && r._test_date && r._commit_status === "pending");
    const readySafe = ready.filter((r) => !hasMismatch(r));
    const readyWarn = ready.filter((r) => hasMismatch(r));
    const errored = results.filter((r) => r.status !== "ok");
    const done = results.filter((r) => r._commit_status === "imported");
    const skipped = results.filter((r) => r._commit_status === "skipped");
    return { total: results.length, ok: ok.length,
             ready: ready.length, ready_safe: readySafe.length, ready_warn: readyWarn.length,
             warn_rows: readyWarn,
             errored: errored.length, done: done.length, skipped: skipped.length };
  }, [results, existingSailors]);

  const commitRow = async (row, force = false) => {
    if (row.status !== "ok" || !row._sailor_id || !row._test_date) return;
    setRow(row._idx, { _commit_status: "committing", _commit_error: null });
    try {
      const markers = row.markers
        .map((m, i) => ({ m, selected: row._markers_selected[i] }))
        .filter(({ m, selected }) => selected && m.value != null && m.value !== "")
        .map(({ m }) => ({
          key: m.canonical_key || m.raw_name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""),
          label: m.canonical_label || m.raw_name,
          unit: m.unit || null,
          value: m.value_is_numeric ? Number(m.value) : String(m.value),
          ref_low: m.ref_low ?? null,
          ref_high: m.ref_high ?? null,
        }));
      if (markers.length === 0) {
        setRow(row._idx, { _commit_status: "error", _commit_error: "No markers selected" });
        return;
      }
      const extraction_source = row.parser_version
        || (row.raw_source ? `claude-${row.raw_source}` : null);
      await api.post("/labs/scan-import/commit", {
        sailor_id: row._sailor_id,
        date: row._test_date,
        markers,
        extraction_source,
        force_duplicate: force,
        pdf_file_id: row.pdf_file_id || null,
      });
      setRow(row._idx, { _commit_status: "imported", _commit_error: null });
      return true;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.code === "duplicate_lab_record") {
        setRow(row._idx, {
          _commit_status: "duplicate",
          _commit_error: detail.message || "Duplicate detected",
        });
        return;
      }
      setRow(row._idx, {
        _commit_status: "error",
        _commit_error: (typeof detail === "string" ? detail : detail?.message) || e.message || "Import failed",
      });
    }
  };

  const commitAll = async () => {
    // Snapshot the queue once so subsequent state updates from
    // commitRow don't reshuffle it mid-loop.
    const queue = results.filter((row) =>
      row.status === "ok"
      && row._commit_status !== "imported"
      && row._sailor_id
      && row._test_date,
    );
    if (queue.length === 0) return;

    // Gate on mismatches — a bulk "Import all" click should never
    // silently attach a lab record to the wrong athlete. If any queued
    // row has a name/gender/off-list mismatch, force an explicit
    // acknowledgement listing every problematic row.
    if (stats.warn_rows.length > 0) {
      const details = stats.warn_rows.slice(0, 10).map((r) => {
        const sel = existingSailors.find((s) => s.id === r._sailor_id);
        return `  • ${r.filename}: "${r._detected_name}" → ${sel?.name || "?"}`;
      }).join("\n");
      const more = stats.warn_rows.length > 10 ? `\n  … and ${stats.warn_rows.length - 10} more` : "";
      const ok = await confirmDialog(
        `${stats.warn_rows.length} row${stats.warn_rows.length === 1 ? "" : "s"} have a name/gender mismatch or a picked athlete that's not in the fuzzy-match candidates:\n\n${details}${more}\n\n` +
        `Click Import all to import ALL rows (including mismatches).\n` +
        `Click Cancel to import only the ${stats.ready_safe} safe row${stats.ready_safe === 1 ? "" : "s"} — mismatch rows will stay staged for individual review.`,
        { confirmLabel: "Import all", danger: true },
      );
      if (!ok) {
        // Filter the queue to safe rows only.
        const warnIds = new Set(stats.warn_rows.map((r) => r._idx));
        const safeQueue = queue.filter((r) => !warnIds.has(r._idx));
        return _runCommitQueue(safeQueue);
      }
    }
    return _runCommitQueue(queue);
  };

  const _runCommitQueue = async (queue) => {
    if (queue.length === 0) { onCommitted?.(); return; }
    setBusy(true); setError("");
    setProgress({
      done: 0, total: queue.length, phase: "importing",
      current: "", in_flight: [],
      stage_label: `Saving ${queue.length} record${queue.length === 1 ? "" : "s"}…`,
    });

    const CONCURRENCY = 5;
    let doneCount = 0;
    let okCount = 0;
    let cursor = 0;
    const inFlight = new Set();

    const worker = async () => {
      while (cursor < queue.length) {
        const row = queue[cursor++];
        inFlight.add(row.filename || `row-${row._idx}`);
        setProgress((p) => ({
          ...p,
          in_flight: [...inFlight],
          current: row.filename || "",
          stage_label: `Saving ${doneCount}/${queue.length}` +
            (inFlight.size > 1 ? ` (${inFlight.size} in-flight)` : ""),
        }));
        if (await commitRow(row)) okCount += 1;
        inFlight.delete(row.filename || `row-${row._idx}`);
        doneCount += 1;
        setProgress((p) => ({
          ...p,
          done: doneCount,
          in_flight: [...inFlight],
          current: row.filename || "",
          stage_label: `${doneCount}/${queue.length} saved` +
            (inFlight.size > 0 ? ` (${inFlight.size} still saving)` : ""),
        }));
      }
    };

    await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));

    setProgress((p) => ({
      ...p, done: queue.length, in_flight: [],
      stage_label: okCount === queue.length
        ? `All ${queue.length} records saved`
        : `${okCount} of ${queue.length} saved — ${queue.length - okCount} need attention`,
    }));
    setBusy(false);
    onCommitted?.();
  };

  const finishAndClose = () => {
    const imported = results.filter((r) => r._commit_status === "imported").length;
    const skipped = results.filter((r) => r._commit_status === "skipped").length;
    const errored = results.filter((r) => r._commit_status === "error" || r._commit_status === "duplicate").length;
    setSummary({ imported, skipped, errored });
    setStep("done");
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto py-6"
      style={{ backdropFilter: "blur(6px)", background: "rgba(11,22,39,0.72)" }}
      onClick={guardedClose}
      data-testid="bulk-lab-scan-wizard"
    >
      <div className="card w-full max-w-6xl mx-4 p-5 md:p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={guardedClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-100"
                data-testid="bulk-lab-scan-close">
          <X size={20} />
        </button>

        <div className="flex items-center gap-3">
          <div className="rounded-lg p-2 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">
            <Layers size={20} />
          </div>
          <div>
            <div className="font-display text-xl text-slate-100">Import lab PDFs</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Drop a single PDF or a whole folder — the wizard auto-detects. Each report
              is extracted in parallel, matched to an athlete, and staged for review
              before anything is saved.
            </div>
          </div>
        </div>

        {step === "upload" && (
          <UploadStep
            onFile={onFiles}
            onDrop={async (dt) => onFiles(await extractFilesFromDrop(dt))}
            fileInputRef={fileInputRef} busy={busy} error={error}
            progress={progress}
          />
        )}
        {step === "review" && (
          <ReviewStep
            results={results} setRow={setRow} existingSailors={existingSailors}
            expanded={expanded} toggleExpand={toggleExpand}
            stats={stats} onCommitRow={commitRow} onCommitAll={commitAll}
            onRetryFailed={retryFailed}
            onFinish={finishAndClose} busy={busy} progress={progress} error={error}
          />
        )}
        {step === "done" && summary && (
          <DoneStep summary={summary} onClose={onClose} />
        )}

        {/* Abandon-work confirm — full-viewport overlay so it never
            gets clipped by a short wizard card (single-file review
            was only ~180 px tall, which cut off the buttons). Uses a
            higher z-index than the wizard shell (9999) so it always
            wins the layer stack. */}
        {confirmClose && (
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center px-4"
            style={{ background: "rgba(11,22,39,0.85)", backdropFilter: "blur(4px)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="bulk-abandon-confirm"
          >
            <div className="card p-5 max-w-md w-full border-amber-500/40">
              <div className="flex items-start gap-3">
                <div className="rounded-lg p-2 bg-amber-500/15 border border-amber-500/40 text-amber-300 shrink-0">
                  <AlertTriangle size={18} />
                </div>
                <div className="min-w-0">
                  <div className="font-display text-slate-100 text-[15px] mb-1">
                    Discard this import?
                  </div>
                  <div className="text-[12px] text-slate-400 leading-relaxed">
                    {busy
                      ? `Extraction is still running (${progress.done || 0}/${progress.total || 0} done). Closing now will cancel it and you'll need to re-upload the folder.`
                      : `You have ${results.filter((r) => r._commit_status === "pending" || r._commit_status === "committing").length} row${results.filter((r) => r._commit_status === "pending" || r._commit_status === "committing").length === 1 ? "" : "s"} not yet saved. Closing now will lose the extracted data and you'll need to re-upload.`}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 mt-4">
                <button
                  onClick={() => setConfirmClose(false)}
                  className="btn-ghost text-[12px]"
                  data-testid="bulk-abandon-cancel"
                >
                  Keep working
                </button>
                <button
                  onClick={() => { setConfirmClose(false); onClose?.(); }}
                  className="btn-primary text-[12px] !bg-rose-500 hover:!bg-rose-600 !border-rose-400"
                  data-testid="bulk-abandon-confirm-btn"
                >
                  Discard and close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ---------- Sub-components ----------

function UploadStep({ onFile, onDrop, fileInputRef, busy, error, progress }) {
  const [drag, setDrag] = useState(false);
  return (
    <div className="mt-6">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault(); setDrag(false);
          onDrop(e.dataTransfer);
        }}
        className={`border-2 border-dashed rounded-xl p-10 text-center transition ${drag ? "border-emerald-400 bg-emerald-500/10" : "border-[#1E304B] hover:border-[#2D4870]"}`}
        data-testid="bulk-dropzone"
      >
        <Layers size={38} className="mx-auto text-slate-400 mb-3" />
        <div className="text-slate-100 mb-1 font-display">
          Drop <strong>one PDF</strong> or a <strong>whole folder</strong> of lab PDFs.
        </div>
        <div className="text-[11px] text-slate-500 mb-4">
          Up to 60 PDFs at a time. Tata 1mg reports process in ~1s each; other formats fall back to Claude.
          <br />
          <span className="text-slate-600">{`Safari can't drop folders — use the "Pick a folder" button instead.`}</span>
        </div>
        {/*
          Folder picker — deliberately NO `accept` filter here.
          Chrome combined with `webkitdirectory` uses the OS-reported MIME
          type to enforce `accept`, which on Linux/macOS often returns
          empty/generic MIME for perfectly valid PDFs. The visible symptom
          was "no PDF files in that folder" even though the folder was
          full of them. We filter by extension in `onFiles()` instead,
          which is reliable across OSes and browsers.
        */}
        <input
          ref={fileInputRef} type="file" multiple
          webkitdirectory=""
          className="hidden" onChange={(e) => onFile(e.target.files)}
          data-testid="bulk-folder-input"
        />
        <input
          ref={(el) => el && (fileInputRef.multiFileInput = el)}
          type="file" accept="application/pdf,.pdf" multiple
          className="hidden"
          onChange={(e) => onFile(e.target.files)}
          data-testid="bulk-files-input"
        />
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => fileInputRef.current?.click()} disabled={busy}
            className="btn-primary text-[12px] disabled:opacity-50" data-testid="bulk-pick-folder">
            {busy
              ? <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Analysing…</span>
              : "Pick a folder"}
          </button>
          <button
            onClick={() => fileInputRef.multiFileInput?.click()} disabled={busy}
            className="btn-ghost text-[12px] disabled:opacity-50" data-testid="bulk-pick-files">
            or pick multiple files
          </button>
        </div>
      </div>
      {/* Progress bar — shown while the streaming endpoint is running.
          Combines a % fill with the current phase / current filename so
          coaches see exactly what the app is doing at each moment. */}
      {busy && progress.total > 0 && (
        <ProgressBar progress={progress} />
      )}
      {error && (
        <div className="mt-3 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-200 px-3 py-2 text-[12px]">{error}</div>
      )}
    </div>
  );
}

function ProgressBar({ progress }) {
  const pct = progress.total > 0
    ? Math.round((progress.done / progress.total) * 100)
    : 0;
  return (
    <div className="mt-4 rounded-lg border border-[#1E304B] bg-[#0F1E33]/60 p-3"
         data-testid="bulk-progress-bar">
      <div className="flex items-center justify-between mb-1.5 text-[11px]">
        <span className="text-slate-300 font-mono-data" data-testid="bulk-progress-label">
          {progress.stage_label || `${progress.done}/${progress.total}`}
        </span>
        <span className="text-cyan-300 font-mono-data" data-testid="bulk-progress-pct">
          {pct}%
        </span>
      </div>
      <div className="h-2 rounded-full bg-[#1E304B] overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400 transition-all duration-300"
          style={{ width: `${pct}%` }}
          data-testid="bulk-progress-fill"
        />
      </div>
      {progress.current && (
        <div className="mt-1.5 text-[10px] text-slate-500 truncate" title={progress.current}>
          Working on: <span className="text-slate-300">{progress.current}</span>
        </div>
      )}
    </div>
  );
}

function ReviewStep({
  results, setRow, existingSailors, expanded, toggleExpand,
  stats, onCommitRow, onCommitAll, onRetryFailed, onFinish, busy, progress, error,
}) {
  return (
    <div className="mt-5">
      {/* Summary bar */}
      <div className="rounded-lg border border-[#1E304B] bg-[#0F1E33]/60 p-3 mb-4 flex flex-wrap gap-4 items-center text-[11px]">
        <span className="text-slate-300"><strong>{stats.total}</strong> files</span>
        <span className="text-emerald-300">· <strong>{stats.ok}</strong> extracted</span>
        {stats.errored > 0 && <span className="text-rose-300">· <strong>{stats.errored}</strong> failed</span>}
        <span className="text-slate-400">· <strong className="text-slate-200">{stats.ready}</strong> ready to import</span>
        {stats.ready_warn > 0 && (
          <span className="text-rose-300 flex items-center gap-1"
                title="Rows where the picked athlete's name/gender doesn't match the PDF, or isn't in the fuzzy-match candidates.">
            <AlertTriangle size={11} /> <strong>{stats.ready_warn}</strong> mismatch
          </span>
        )}
        {stats.done > 0 && <span className="text-cyan-300">· <strong>{stats.done}</strong> imported</span>}
        <div className="ml-auto flex items-center gap-2">
          {stats.errored > 0 && (
            <button
              onClick={onRetryFailed}
              disabled={busy}
              className="btn-ghost text-[12px] disabled:opacity-50 border border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
              title="Re-send only the files that failed extraction. Handy after a transient Claude stream hiccup."
              data-testid="bulk-retry-failed"
            >
              {busy && progress.phase === "extracting"
                ? <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Retrying…</span>
                : `↻ Retry failed (${stats.errored})`}
            </button>
          )}
          <button onClick={onCommitAll} disabled={busy || stats.ready === 0}
                  className="btn-primary text-[12px] disabled:opacity-50" data-testid="bulk-commit-all">
            {busy && progress.phase === "importing"
              ? <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Importing {progress.done}/{progress.total}…</span>
              : `Import all ready (${stats.ready}) →`}
          </button>
          <button onClick={onFinish} className="btn-ghost text-[12px]" data-testid="bulk-finish">Finish</button>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-200 px-3 py-2 text-[12px]">{error}</div>
      )}

      {busy && progress.phase === "importing" && progress.total > 0 && (
        <div className="mb-3">
          <ProgressBar progress={progress} />
        </div>
      )}

      {/* Grid */}
      <div className="rounded-lg border border-[#1E304B] max-h-[62vh] overflow-y-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-[#0F1E33] text-[10px] uppercase tracking-wider text-slate-400 sticky top-0 z-10">
            <tr>
              <th className="text-center px-2 py-2 font-normal w-6"></th>
              <th className="text-left px-3 py-2 font-normal">File</th>
              <th className="text-left px-3 py-2 font-normal">Detected</th>
              <th className="text-left px-3 py-2 font-normal">Athlete</th>
              <th className="text-left px-3 py-2 font-normal">Test date</th>
              <th className="text-left px-3 py-2 font-normal">Markers</th>
              <th className="text-left px-3 py-2 font-normal">Status</th>
              <th className="text-right px-3 py-2 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {results.map((row) => (
              <BulkRow
                key={row._idx} row={row} setRow={setRow}
                existingSailors={existingSailors}
                expanded={expanded.has(row._idx)}
                onToggleExpand={() => toggleExpand(row._idx)}
                onCommit={() => onCommitRow(row)}
                onForceCommit={() => onCommitRow(row, true)}
                onSkip={() => setRow(row._idx, { _commit_status: "skipped" })}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BulkRow({ row, setRow, existingSailors, expanded, onToggleExpand, onCommit, onForceCommit, onSkip }) {
  const isError = row.status !== "ok";
  const isDone = row._commit_status === "imported";
  const isSkipped = row._commit_status === "skipped";
  const isCommitting = row._commit_status === "committing";
  const isDuplicate = row._commit_status === "duplicate";
  const isCommitErr = row._commit_status === "error";

  const selectedMarkers = row._markers_selected
    ? row._markers_selected.filter(Boolean).length
    : 0;
  const newMarkers = row.markers?.filter((m, i) => m.is_new_marker && row._markers_selected?.[i]).length || 0;

  // -------- Mismatch detection -----------------------------------------
  // Compare the PDF's detected patient (name + gender) against whichever
  // athlete the operator has selected in the dropdown. Renders as an
  // amber ⚠ badge next to the picker so a coach can't silently attach
  // Nithin's blood work to a female athlete named Mani.
  const detectedName = (row._detected_name || "").toLowerCase();
  const detectedGender = row.extracted?.patient_gender || "";
  const selectedSailor = existingSailors.find((s) => s.id === row._sailor_id);
  const selectedName = (selectedSailor?.name || "").toLowerCase();
  const selectedGender = selectedSailor?.gender || "";

  // Normalise both `"Male"`/`"Female"` and `"M"`/`"F"` to a single
  // letter so the mismatch check works regardless of which format the
  // baseline row uses (older baselines saved short codes).
  const normGender = (g) => {
    const c = (g || "").trim().toLowerCase().charAt(0);
    return c === "m" ? "m" : c === "f" ? "f" : "";
  };

  // Simple token overlap — if the detected patient name and the picked
  // athlete's name share ANY token of length >=3, we treat the name as
  // "plausibly matching". Otherwise it's a hard mismatch (e.g. "N Mani"
  // picked against "Ravi Kumar").
  const nameTokens = (s) => new Set(
    s.split(/\s+/).map((t) => t.replace(/[^a-z]/g, "")).filter((t) => t.length >= 3),
  );
  const nameMismatch = selectedSailor && detectedName && (() => {
    const a = nameTokens(detectedName);
    const b = nameTokens(selectedName);
    for (const t of a) if (b.has(t)) return false;
    return true;
  })();
  const genderMismatch = selectedSailor
    && detectedGender && selectedGender
    && normGender(detectedGender) !== normGender(selectedGender)
    && normGender(detectedGender) !== ""
    && normGender(selectedGender) !== "";

  // Warn if the operator picked an athlete NOT in the top-6 candidates
  // list — the backend's fuzzy matcher didn't even consider them a
  // plausible match, so it's almost certainly a wrong pick.
  const candidateIds = new Set((row.candidates || []).map((c) => c.sailor_id));
  const offList = selectedSailor
    && candidateIds.size > 0
    && !candidateIds.has(row._sailor_id);

  const hasWarnings = nameMismatch || genderMismatch || offList;

  const rowBg = isDone ? "bg-emerald-500/5"
    : isSkipped ? "bg-slate-500/5 opacity-60"
    : isDuplicate || isCommitErr ? "bg-rose-500/5"
    : isError ? "bg-rose-500/5"
    : hasWarnings ? "bg-rose-500/10"    // stronger than needs-review
    : row._needs_review ? "bg-amber-500/5"
    : "";

  // Prevent silent wrong imports — the Import button turns into a
  // "confirm mismatch" gate when warnings are present.
  const commitBtnLabel = hasWarnings ? "Confirm mismatch" : "Import";

  const handleCommitClick = async () => {
    if (hasWarnings) {
      const reasons = [];
      if (nameMismatch) reasons.push(`Patient name "${row._detected_name}" doesn't match athlete "${selectedSailor.name}"`);
      if (genderMismatch) reasons.push(`Patient gender (${detectedGender}) doesn't match athlete gender (${selectedGender})`);
      if (offList) reasons.push(`"${selectedSailor.name}" is not in the fuzzy-matched candidate list`);
      const ok = await confirmDialog(
        `Import anyway?\n\n${reasons.map((r) => `• ${r}`).join("\n")}\n\n` +
        `If this is wrong, the lab record will be attached to the wrong athlete.`,
        { confirmLabel: "Import anyway", danger: true },
      );
      if (!ok) return;
    }
    onCommit(row);
  };

  return (
    <>
      <tr className={`border-t border-[#1E304B] ${rowBg}`} data-testid={`bulk-row-${row._idx}`}>
        <td className="px-2 py-1.5 text-center">
          <button onClick={onToggleExpand} className="text-slate-400 hover:text-slate-100" disabled={isError}>
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        </td>
        <td className="px-3 py-1.5 text-slate-200 font-mono-data truncate max-w-[220px]" title={row.filename}>
          {row.filename}
        </td>
        <td className="px-3 py-1.5 text-slate-300 font-mono-data text-[11px]">
          {isError ? <span className="text-rose-300">—</span> : (row._detected_name || "—")}
        </td>
        <td className="px-3 py-1.5">
          {isError ? <span className="text-rose-300 text-[11px]">extraction failed</span>
            : (
              <div className="flex items-center gap-1.5">
                <select
                  value={row._sailor_id}
                  onChange={(e) => setRow(row._idx, { _sailor_id: e.target.value })}
                  className={`form-input text-[11px] !py-0.5 w-full min-w-[220px] ${hasWarnings ? "!border-rose-400" : ""}`}
                  data-testid={`bulk-sailor-${row._idx}`}
                >
                  <option value="">— pick —</option>
                  {(row.candidates || []).length > 0 && (
                    <optgroup label="Top matches (from patient name)">
                      {(row.candidates || []).map((c) => {
                        const pct = Math.round((c.score || 0) * 100);
                        const gtag = c.gender ? ` · ${c.gender.slice(0, 1)}` : "";
                        const dup = c.has_lab_on_date ? "  ⚠ has lab" : "";
                        return (
                          <option key={`cand-${c.sailor_id}`} value={c.sailor_id}>
                            {`${pct}%  ·  ${c.name}${c.group ? " · " + c.group : ""}${gtag}${dup}`}
                          </option>
                        );
                      })}
                    </optgroup>
                  )}
                  <optgroup label="All athletes">
                    {existingSailors.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.group ? ` · ${s.group}` : ""}{s.gender ? ` · ${s.gender.slice(0, 1)}` : ""}
                      </option>
                    ))}
                  </optgroup>
                </select>
                {row._ambiguous && (
                  <span
                    className="text-amber-300 shrink-0"
                    title={
                      "Multiple athletes match this name equally well. " +
                      "Confirm the correct one before committing — we did " +
                      "not auto-pick to avoid a wrong assignment."
                    }
                    data-testid={`bulk-ambiguous-${row._idx}`}
                  >
                    <AlertTriangle size={13} />
                  </span>
                )}
                {hasWarnings && (
                  <span
                    className="text-rose-300 shrink-0"
                    title={[
                      nameMismatch && `Name mismatch: "${row._detected_name}" vs "${selectedSailor?.name}"`,
                      genderMismatch && `Gender mismatch: ${detectedGender} vs ${selectedGender}`,
                      offList && `Not in fuzzy-match candidates`,
                    ].filter(Boolean).join(" · ")}
                    data-testid={`bulk-mismatch-${row._idx}`}
                  >
                    <AlertTriangle size={13} />
                  </span>
                )}
              </div>
            )}
        </td>
        <td className="px-3 py-1.5">
          {isError ? "—" : (
            <input type="date" value={row._test_date}
                   onChange={(e) => setRow(row._idx, { _test_date: e.target.value })}
                   className="form-input text-[11px] !py-0.5"
                   data-testid={`bulk-date-${row._idx}`} />
          )}
        </td>
        <td className="px-3 py-1.5 text-slate-300 text-[11px] whitespace-nowrap">
          {isError ? "—" : (
            <>
              <strong className="text-slate-100">{selectedMarkers}</strong>
              <span className="text-slate-500">/{row.markers?.length || 0}</span>
              {newMarkers > 0 && <span className="text-rose-300 ml-1">· {newMarkers} new</span>}
            </>
          )}
        </td>
        <td className="px-3 py-1.5">
          {isError && (
            <span className="text-rose-300 text-[11px] flex items-center gap-1" title={row.error}>
              <AlertTriangle size={10} /> Failed
            </span>
          )}
          {!isError && isDone && (
            <span className="text-emerald-300 text-[11px] flex items-center gap-1">
              <CheckCircle2 size={10} /> Imported
            </span>
          )}
          {!isError && isSkipped && <span className="text-slate-400 text-[11px]">Skipped</span>}
          {!isError && isCommitting && <span className="text-cyan-300 text-[11px] flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> …</span>}
          {!isError && isDuplicate && <span className="text-rose-300 text-[11px] flex items-center gap-1" title={row._commit_error}><AlertTriangle size={10} /> Duplicate</span>}
          {!isError && isCommitErr && <span className="text-rose-300 text-[11px] flex items-center gap-1" title={row._commit_error}><AlertTriangle size={10} /> Error</span>}
          {!isError && row._commit_status === "pending" && (
            row._sailor_id && row._test_date
              ? <span className="text-slate-300 text-[11px]">Ready</span>
              : <span className="text-amber-300 text-[11px] flex items-center gap-1"><AlertTriangle size={10} /> Needs review</span>
          )}
        </td>
        <td className="px-3 py-1.5 text-right whitespace-nowrap">
          {!isError && !isDone && !isSkipped && (
            <>
              {isDuplicate ? (
                <button onClick={onForceCommit}
                        className="btn-ghost !py-0.5 !px-2 text-[11px] !text-rose-300"
                        title="Import as second record"
                        data-testid={`bulk-force-${row._idx}`}>Force</button>
              ) : (
                <button onClick={handleCommitClick}
                        disabled={!row._sailor_id || !row._test_date || isCommitting}
                        className={`btn-primary !py-0.5 !px-2 text-[11px] disabled:opacity-40 ${hasWarnings ? "!bg-rose-500 hover:!bg-rose-600 !border-rose-400" : ""}`}
                        title={hasWarnings ? "Mismatch detected — click for details" : undefined}
                        data-testid={`bulk-commit-${row._idx}`}>{commitBtnLabel}</button>
              )}
              <button onClick={onSkip}
                      className="btn-ghost !py-0.5 !px-2 text-[11px] ml-1"
                      data-testid={`bulk-skip-${row._idx}`}>Skip</button>
            </>
          )}
        </td>
      </tr>
      {expanded && !isError && (
        <tr className="bg-[#0B1627]">
          <td colSpan={8} className="px-4 py-3">
            <div className="text-[11px] text-slate-400 mb-2 flex items-center gap-2">
              <Sparkles size={11} className={row.raw_source === "deterministic" ? "text-emerald-300" : "text-cyan-300"} />
              source: <span className="font-mono-data text-slate-200">
                {row.parser_version || row.raw_source}
              </span>
              {row.raw_source === "deterministic" && (
                <span className="text-emerald-300 text-[10px]">· fast path ⚡</span>
              )}
              <span className="ml-3">Top matches:</span>
              {(row.candidates || []).slice(0, 3).map((c) => (
                <button key={c.sailor_id}
                        onClick={() => setRow(row._idx, { _sailor_id: c.sailor_id })}
                        className="underline hover:text-cyan-300 mr-2">
                  {c.name} ({(c.score * 100).toFixed(0)}%){c.has_lab_on_date ? " ⚠" : ""}
                </button>
              ))}
            </div>
            {/* Marker table (compact) */}
            <div className="rounded-md border border-[#1E304B] max-h-[220px] overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-[#0F1E33] text-[9px] uppercase tracking-wider text-slate-400 sticky top-0">
                  <tr>
                    <th className="text-center px-2 py-1.5 font-normal w-6"></th>
                    <th className="text-left px-2 py-1.5 font-normal">Marker</th>
                    <th className="text-left px-2 py-1.5 font-normal">Value</th>
                    <th className="text-left px-2 py-1.5 font-normal">Unit</th>
                    <th className="text-left px-2 py-1.5 font-normal">Maps to</th>
                  </tr>
                </thead>
                <tbody>
                  {row.markers.map((m, i) => (
                    <tr key={`${m.raw_name}-${i}`} className="border-t border-[#1E304B]">
                      <td className="px-2 py-0.5 text-center">
                        <input type="checkbox" checked={!!row._markers_selected?.[i]}
                               onChange={(e) => {
                                 const arr = [...(row._markers_selected || [])];
                                 arr[i] = e.target.checked;
                                 setRow(row._idx, { _markers_selected: arr });
                               }}
                               className="accent-cyan-400" />
                      </td>
                      <td className="px-2 py-0.5 text-slate-200 font-mono-data">{m.raw_name}</td>
                      <td className="px-2 py-0.5 font-mono-data text-slate-100">{String(m.value ?? "")}</td>
                      <td className="px-2 py-0.5 text-slate-400 font-mono-data">{m.unit || "—"}</td>
                      <td className="px-2 py-0.5">
                        {m.canonical_key ? (
                          <span className="text-emerald-300 font-mono-data">{m.canonical_key}</span>
                        ) : m.is_qualitative ? (
                          <span className="text-amber-300"><FileText size={9} className="inline" /> note</span>
                        ) : (
                          <span className="text-rose-300"><span className="font-semibold">+ new</span></span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {row._commit_error && (
              <div className="mt-2 text-[11px] text-rose-300">{row._commit_error}</div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function DoneStep({ summary, onClose }) {
  return (
    <div className="mt-6 text-center py-8">
      <div className="rounded-full p-4 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 inline-block mb-4">
        <CheckCircle2 size={40} />
      </div>
      <div className="font-display text-2xl text-slate-100 mb-2">Bulk import complete</div>
      <div className="text-[12px] text-slate-400 mb-6">
        <span className="text-emerald-300">✓ {summary.imported} imported</span>
        {summary.skipped > 0 && <span className="ml-3">— {summary.skipped} skipped</span>}
        {summary.errored > 0 && <span className="ml-3 text-rose-300">✗ {summary.errored} errored</span>}
      </div>
      <button onClick={onClose} className="btn-primary text-[12px]" data-testid="bulk-close-done">Close</button>
    </div>
  );
}
